import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar as CalendarIcon, Plus, RefreshCw, Settings as SettingsIcon } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCalendarFeed } from '../hooks/useCalendarFeed';
import { useScheduledEvents } from '../hooks/useScheduledEvents';
import { useCalendarSync } from '../hooks/useCalendarSync';
import CalendarView from '../components/calendar/CalendarView';
import CalendarFilters from '../components/calendar/CalendarFilters';
import EventCreateModal from '../components/calendar/EventCreateModal';
import EventDetailModal from '../components/calendar/EventDetailModal';
import CalendarSyncSettings from '../components/calendar/CalendarSyncSettings';

const KNOWN_COLABORADORES = new Set(['caroline', 'eduardo', 'fabio']);

function colaboradorFromEmail(email) {
  if (!email) return null;
  const prefix = email.split('@')[0]?.toLowerCase();
  return prefix && KNOWN_COLABORADORES.has(prefix) ? prefix : null;
}

export default function AgendaPage() {
  const { user } = useAuth();
  const currentColaboradorId = useMemo(() => colaboradorFromEmail(user?.email), [user?.email]);
  const [filters, setFilters] = useState({ colaboradorId: 'all', businessUnitId: '', eventType: '' });
  const { events, loading, error, refetch } = useCalendarFeed(filters);
  const { updateEvent } = useScheduledEvents();
  const { syncFeeds, syncing } = useCalendarSync(currentColaboradorId);
  const [syncFeedback, setSyncFeedback] = useState(null);

  const [createState, setCreateState] = useState({ open: false, start: null, end: null, allDay: false });
  const [detailState, setDetailState] = useState({ open: false, eventRef: null });
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleDateSelect = useCallback((info) => {
    setCreateState({ open: true, start: info.start, end: info.end, allDay: info.allDay });
  }, []);

  const handleEventClick = useCallback((fcEvent) => {
    const props = fcEvent.extendedProps;
    setDetailState({
      open: true,
      eventRef: {
        sourceId: props.sourceId,
        sourceTable: props.sourceTable,
        title: fcEvent.title,
        status: props.status,
        startAt: fcEvent.start ? fcEvent.start.toISOString() : null,
        endAt: fcEvent.end ? fcEvent.end.toISOString() : null,
        allDay: fcEvent.allDay,
        description: props.description,
        location: props.location,
        attendees: props.attendees,
        organizer: props.organizer,
        meetUrl: props.meetUrl,
        ownerColaboradorId: props.ownerColaboradorId,
        colaboradorColor: props.colaboradorColor,
      },
    });
  }, []);

  const handleEventDrop = useCallback(async ({ id, startAt, endAt }) => {
    const { error: err } = await updateEvent(id, { startAt, endAt });
    if (err) {
      console.error('eventDrop failed:', err);
      return false;
    }
    refetch();
    return true;
  }, [updateEvent, refetch]);

  const handleEventResize = handleEventDrop;

  const handleQuickSync = useCallback(async () => {
    setSyncFeedback(null);
    const { data, error: err } = await syncFeeds();
    if (err) {
      setSyncFeedback({ kind: 'error', msg: err });
    } else {
      setSyncFeedback({
        kind: 'ok',
        msg: `${data?.events_upserted ?? 0} eventos sincronizados${data?.events_removed ? ` · ${data.events_removed} removidos` : ''}.`,
      });
      refetch();
    }
    setTimeout(() => setSyncFeedback(null), 5000);
  }, [syncFeeds, refetch]);

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-3 flex-shrink-0">
        <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft size={14} /> Dashboard
        </Link>
        <div className="border-l border-slate-200 pl-3 flex-1 min-w-0">
          <h1 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <CalendarIcon size={16} className="text-blue-600" /> Agenda
          </h1>
        </div>
        <button
          onClick={handleQuickSync}
          disabled={syncing || !currentColaboradorId}
          title={currentColaboradorId ? 'Sincronizar calendários externos' : 'Logue como eduardo/fabio/caroline para usar sync'}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-50"
        >
          <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Sincronizando...' : 'Sincronizar'}
        </button>
        <button
          onClick={() => setSettingsOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 rounded-lg"
          title="Configurar sincronização"
        >
          <SettingsIcon size={14} />
        </button>
        <button
          onClick={() => setCreateState({ open: true, start: null, end: null, allDay: false })}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={14} /> Novo evento
        </button>
      </header>

      <div className="bg-white border-b border-slate-100 px-4 py-2 flex-shrink-0">
        <CalendarFilters filters={filters} onChange={setFilters} />
      </div>

      {(syncFeedback || error) && (
        <div className="px-4 py-2 space-y-2 flex-shrink-0">
          {syncFeedback && (
            <div className={`text-sm border rounded p-2 ${syncFeedback.kind === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {syncFeedback.msg}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
              Erro ao carregar agenda: {error}
            </div>
          )}
        </div>
      )}

      <main className="flex-1 min-h-0 p-2">
        <CalendarView
          events={events}
          loading={loading}
          onDateSelect={handleDateSelect}
          onEventClick={handleEventClick}
          onEventDrop={handleEventDrop}
          onEventResize={handleEventResize}
          height="100%"
        />
      </main>

      <EventCreateModal
        open={createState.open}
        onClose={() => setCreateState({ open: false, start: null, end: null, allDay: false })}
        onCreated={() => refetch()}
        initialStart={createState.start}
        initialEnd={createState.end}
        initialAllDay={createState.allDay}
        currentUserEmail={user?.email}
      />

      <EventDetailModal
        open={detailState.open}
        onClose={() => setDetailState({ open: false, eventRef: null })}
        eventRef={detailState.eventRef}
        onChanged={() => refetch()}
      />

      <CalendarSyncSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        colaboradorId={currentColaboradorId}
        onAfterSync={() => refetch()}
      />
    </div>
  );
}
